/**
 * The film archive: load a saved war record (`.friction-replay`, from the after-action report or
 * The Fog Lifts) — by picking or dropping the file — see its card (theatre, nations, victors,
 * length, when it was saved, whether the rules it was recorded under are ours) and screen it in
 * the replay viewer. Every file is validated before anything is shown (replay/file.ts).
 */
import { dayOf, SCENARIO_IDS, type ScenarioId } from '@friction/engine';
import { REPLAY_FILE_EXTENSION, type ReplayFileHeader, sameRules } from '@friction/protocol';
import { type DragEvent, useRef, useState } from 'react';
import { bus } from '../../game/bus';
import { cssColor } from '../../game/format';
import { type Translator, useT } from '../../i18n';
import { nationName, theatreTitle } from '../../i18n/names';
import { archiveStore, loadRecord, useArchive } from '../../replay/file';
import { openArchiveReplay } from '../../replay/store';
import { Button, cx } from '../common/controls';
import { Icon } from '../common/Icon';
import { Paper } from '../common/Panel';
import a from './archive.module.css';
import { ScreenFrame } from './ScreenFrame';
import s from './screens.module.css';

const errorKey = {
  tooLarge: 'arc.err.tooLarge',
  notReplay: 'arc.err.notReplay',
  newer: 'arc.err.newer',
  version: 'arc.err.version',
  corrupt: 'arc.err.corrupt',
  invalid: 'arc.err.invalid',
  read: 'arc.err.read',
} as const;

function savedOn(t: Translator, iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(t.locale === 'de' ? 'de-DE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function RecordCard({ header }: { header: ReplayFileHeader }) {
  const t = useT();
  const same = sameRules(header);
  const days = dayOf(header.endTick);
  const victors = header.winners?.map((id) => header.factions[id]).filter((f) => f !== undefined) ?? [];
  const scenario = SCENARIO_IDS.find((id: ScenarioId) => id === header.scenario);
  return (
    <Paper className={cx(s.briefing, a.card)}>
      <div className={cx(s.briefHead, a.cardHead)}>
        <span>{t('arc.card.head')}</span>
        <span>{t('arc.saved', { date: savedOn(t, header.savedAt) })}</span>
      </div>
      <div className={a.theatre}>{theatreTitle(header.theatre, t.locale)}</div>
      <div className={a.length}>
        {t('arc.length', { n: days })}
        {scenario ? ` · ${t('arc.scenario')}: ${t(`scn.${scenario}.title`)}` : ''}
      </div>
      <div className={a.head}>{t('arc.nations')}</div>
      <ul className={a.nations}>
        {header.factions.map((f) => (
          <li key={`${f.name}-${f.color}`}>
            <i style={{ background: cssColor(f.color) }} />
            <span className={a.nation}>{nationName(f, t.locale)}</span>
            <span className={a.who}>{f.human ? t('arc.commander') : t('arc.computer')}</span>
          </li>
        ))}
      </ul>
      <div className={s.briefRows}>
        <span className={s.briefKey}>{t('arc.victors')}</span>
        <span className={s.briefVal}>
          {victors.length ? victors.map((f) => nationName(f, t.locale)).join(' · ') : t('arc.noVictor')}
        </span>
      </div>
      <p className={cx(a.rules, !same && a.rulesOther)}>
        {same ? t('arc.rules.same') : t('arc.rules.other')}
      </p>
    </Paper>
  );
}

export function ArchiveScreen() {
  const t = useT();
  const file = useArchive((x) => x.file);
  const loading = useArchive((x) => x.loading);
  const error = useArchive((x) => x.error);
  const name = useArchive((x) => x.name);
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const pick = (f: File | undefined) => {
    if (!f) return;
    bus.emit('sound', { name: 'paper' });
    void loadRecord(f);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    pick(e.dataTransfer.files[0]);
  };

  return (
    <ScreenFrame
      kicker={t('arc.kicker')}
      title={t('arc.title')}
      footer={<span className={s.note}>{t('arc.footer')}</span>}
    >
      <div className={a.layout}>
        <section className={s.section}>
          <div
            className={cx(a.drop, over && a.dropOver)}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
          >
            <Icon name="reel" size={40} />
            <Button variant="primary" icon="reel" disabled={loading} onClick={() => input.current?.click()}>
              {loading ? t('arc.reading') : file ? t('arc.another') : t('arc.load')}
            </Button>
            <span className={a.dropHint}>{t('arc.drop')}</span>
            {name && file && <span className={a.fileName}>{name}</span>}
            <input
              ref={input}
              type="file"
              accept={REPLAY_FILE_EXTENSION}
              className={a.hidden}
              onChange={(e) => {
                pick(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </div>
          {error && (
            <div className={s.error} role="alert">
              <Icon name="close" />
              <span>{t(errorKey[error])}</span>
              <Button variant="ghost" size="sm" onClick={() => archiveStore.setState({ error: null })}>
                {t('ui.dismiss')}
              </Button>
            </div>
          )}
        </section>

        <aside className={a.side}>
          {file ? (
            <>
              <RecordCard header={file.header} />
              <Button
                variant="primary"
                size="lg"
                block
                icon="play"
                onClick={() => {
                  bus.emit('sound', { name: 'click' });
                  openArchiveReplay(file.log);
                }}
              >
                {t('arc.screen')}
              </Button>
            </>
          ) : (
            <div className={s.empty}>
              <Icon name="reel" size={34} />
              <div className={s.emptyTitle}>{t('arc.empty.title')}</div>
              <div>{t('arc.empty.text')}</div>
            </div>
          )}
        </aside>
      </div>
    </ScreenFrame>
  );
}
